import { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Switch,
  CircularProgress,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { useApi } from "../../hooks/useApi";
import { useAuth } from "../../context/AuthContext";
import { useTenant } from "../../hooks/useTenant";

const fmt = (n) =>
  Number(n).toLocaleString("en-US", { style: "currency", currency: "USD" });

const BarberServices = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const api = useApi();
  const { user } = useAuth();
  const { tenant } = useTenant();

  // Owners/admins always edit. A barber is locked to read-only when the shop
  // has turned off "Allow barbers to edit their services" (server also enforces).
  const readOnly =
    user?.role === "barber" && tenant?.allow_barber_edit_services === false;

  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const [editOpen, setEditOpen] = useState(false);
  const [editService, setEdit] = useState(null);
  const [editPrice, setEditPrice] = useState("");
  const [editDuration, setEditDur] = useState("");

  const load = async () => {
    if (!user?.user_id) return;
    setLoading(true);
    try {
      const res = await api(`/barbers/services.php?barber_id=${user.user_id}`, {
        method: "GET",
      });
      setServices(res.data.services || []);
    } catch {
      setError("Failed to load services.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [user?.user_id]);

  const saveAll = async (updated) => {
    setSaving(true);
    setError(null);
    setSuccess(false);
    try {
      await api(`/barbers/services.php?barber_id=${user.user_id}`, {
        method: "PUT",
        body: JSON.stringify({
          services: updated.map((s) => ({
            id: s.id,
            name: s.name,
            description: s.description || "",
            price: parseFloat(s.price),
            duration_minutes: parseInt(s.duration_minutes),
            is_active: s.is_active ? 1 : 0,
          })),
        }),
      });
      setSuccess(true);
      await load();
    } catch (err) {
      setError(err?.message || "Failed to save services.");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = (id) => {
    const updated = services.map((s) =>
      s.id === id ? { ...s, is_active: s.is_active ? 0 : 1 } : s,
    );
    setServices(updated);
    saveAll(updated);
  };

  const openEdit = (s) => {
    setEdit(s);
    setEditPrice(String(s.price));
    setEditDur(String(s.duration_minutes));
    setEditOpen(true);
  };

  const saveEdit = () => {
    const updated = services.map((s) =>
      s.id === editService.id
        ? {
            ...s,
            price: parseFloat(editPrice) || s.price,
            duration_minutes: parseInt(editDuration) || s.duration_minutes,
          }
        : s,
    );
    setEditOpen(false);
    setEdit(null);
    saveAll(updated);
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: 300,
        }}
      >
        <CircularProgress size={28} />
      </Box>
    );
  }

  const active = services.filter((s) => s.is_active);
  const inactive = services.filter((s) => !s.is_active);

  const ServiceRow = ({ s }) => (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        px: 3,
        py: 2,
        borderBottom: "0.1px solid",
        borderColor: "customDivider",
        "&:last-child": { borderBottom: "none" },
      }}
    >
      {/* Row 1: Name + price */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {s.name}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {fmt(s.price)}
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>·</Typography>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            {s.duration_minutes} min
          </Typography>
          {parseFloat(s.price) !== parseFloat(s.shop_price) && (
            <Chip
              size="small"
              label={`Shop: ${fmt(s.shop_price)}`}
              sx={{ fontSize: 11, height: 20, backgroundColor: "#f5f5f5" }}
            />
          )}
        </Box>
      </Box>

      {/* Row 2: Description + actions */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 0.5 }}>
        <Typography variant="body2" sx={{ color: "text.secondary", fontSize: 12, flex: 1, maxWidth: "55%", mr: 1 }}>
          {s.description || ""}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
          {!readOnly && (
            <Button
              size="small"
              onClick={() => openEdit(s)}
              sx={{ textTransform: "none", fontSize: 12, color: "text.secondary", minWidth: 0, px: 1 }}
            >
              Edit
            </Button>
          )}
          <Switch
            size="small"
            checked={!!s.is_active}
            onChange={() => toggleActive(s.id)}
            disabled={saving || readOnly}
          />
        </Box>
      </Box>
    </Box>
  );

  return (
    <Box
      sx={{ maxWidth: 1100, mx: "auto", px: { xs: 3, md: 5 }, pt: 5, pb: 10 }}
    >
      <Box sx={{ mb: 4 }}>
        <Typography variant={isMobile ? "h4" : "h3"} sx={{ fontWeight: 700, lineHeight: 1.2 }}>
          Services
        </Typography>
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          Toggle services on or off and adjust your personal pricing and duration.
        </Typography>
      </Box>

      {readOnly && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Your administrator manages services for this shop. This page is
          read-only.
        </Alert>
      )}

      {success && (
        <Alert
          severity="success"
          sx={{ mb: 3 }}
          onClose={() => setSuccess(false)}
        >
          Services updated successfully.
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Active services */}
      {active.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="overline"
            sx={{ color: "text.secondary", display: "block", mb: 1 }}
          >
            Active ({active.length})
          </Typography>
          <Box
            sx={{
              borderRadius: 2,
              border: "0.1px solid",
              borderColor: "customDivider",
              backgroundColor: "offWhite",
              overflow: "hidden",
            }}
          >
            {active.map((s) => (
              <ServiceRow key={s.id} s={s} />
            ))}
          </Box>
        </Box>
      )}

      {/* Inactive services */}
      {inactive.length > 0 && (
        <Box>
          <Typography
            variant="overline"
            sx={{ color: "text.secondary", display: "block", mb: 1 }}
          >
            Inactive ({inactive.length})
          </Typography>
          <Box
            sx={{
              borderRadius: 2,
              border: "0.1px solid",
              borderColor: "customDivider",
              backgroundColor: "offWhite",
              overflow: "hidden",
              opacity: 0.7,
            }}
          >
            {inactive.map((s) => (
              <ServiceRow key={s.id} s={s} />
            ))}
          </Box>
        </Box>
      )}

      {services.length === 0 && (
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          No services assigned yet. Contact your shop owner to add services.
        </Typography>
      )}

      {/* Edit dialog */}
      <Dialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600 }}>
          Edit {editService?.name}
        </DialogTitle>
        <DialogContent sx={{ "&&": { pt: 2 } }}>
          <TextField
            label="Your Price ($)"
            type="number"
            value={editPrice}
            onChange={(e) => setEditPrice(e.target.value)}
            fullWidth
            size="small"
            sx={{ mb: 2 }}
            helperText={`Shop default: ${fmt(editService?.shop_price)}`}
          />
          <TextField
            label="Duration (minutes)"
            type="number"
            value={editDuration}
            onChange={(e) => setEditDur(e.target.value)}
            fullWidth
            size="small"
                        helperText={`Shop default: ${editService?.shop_duration} min`}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setEditOpen(false)}
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={saveEdit}
            sx={{
              textTransform: "none",
              backgroundColor: "#9c6a36",
              "&:hover": { backgroundColor: "#7a5229" },
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BarberServices;
